import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { DataTableModuleConfiguration } from '../data-table/datatable-module.config';
import { ACTING_ORGANIZATION_ID_PROVIDER } from './current-organization-id.provider';

export type TableConfigurationSource = 'USER' | 'ORG_DEFAULT';

export interface TableConfiguration {
  tableIdentifier: string;
  visibleColumns: string[];
  source: TableConfigurationSource;
}

@Injectable({
  providedIn: 'root',
})
export class TableConfigurationService {
  private http = inject(HttpClient);
  private configuration = inject(DataTableModuleConfiguration);
  private getCurrentOrganizationId = inject(ACTING_ORGANIZATION_ID_PROVIDER);

  private readonly noOrganizationCacheKey = '__no_org__';
  private cache = new Map<string, TableConfiguration | null>();

  private get baseUrl(): string {
    return `${this.configuration.apiUrls.workflow}/api/v1/table-configurations`;
  }

  getConfiguration(tableId: string): Observable<TableConfiguration | null> {
    const cacheKey = this.createCacheKey(tableId);
    if (this.cache.has(cacheKey)) {
      return of(this.cache.get(cacheKey) ?? null);
    }

    return this.http
      .get<TableConfiguration | null>(`${this.baseUrl}/${tableId}`)
      .pipe(
        tap((config) => this.cache.set(cacheKey, config)),
        catchError((error: HttpErrorResponse) => {
          if (error.status === 404) {
            this.cache.set(cacheKey, null);
            return of(null);
          }
          return throwError(() => error);
        }),
      );
  }

  saveConfiguration(
    tableId: string,
    visibleColumns: string[],
  ): Observable<TableConfiguration> {
    const cacheKey = this.createCacheKey(tableId);
    const request = { visibleColumns };
    const previousConfig = this.cache.get(cacheKey);
    this.cache.set(cacheKey, {
      tableIdentifier: tableId,
      visibleColumns: [...visibleColumns],
      source: 'USER',
    });

    return this.http
      .put<TableConfiguration>(`${this.baseUrl}/${tableId}`, request)
      .pipe(
        tap((config) => this.cache.set(cacheKey, config)),
        catchError((error) => {
          if (previousConfig) {
            this.cache.set(cacheKey, previousConfig);
          } else {
            this.cache.delete(cacheKey);
          }
          return throwError(() => error);
        }),
        map((config) => config),
      );
  }

  clearCache(tableId?: string): void {
    if (tableId) {
      this.cache.delete(this.createCacheKey(tableId));
    } else {
      this.cache.clear();
    }
  }

  deleteConfiguration(tableId: string): Observable<void> {
    this.cache.delete(this.createCacheKey(tableId));
    return this.http.delete<void>(`${this.baseUrl}/${tableId}`);
  }

  saveOrganizationDefault(
    tableId: string,
    visibleColumns: string[],
  ): Observable<TableConfiguration> {
    const cacheKey = this.createCacheKey(tableId);
    const previousConfig = this.cache.get(cacheKey);
    this.cache.set(cacheKey, {
      tableIdentifier: tableId,
      visibleColumns: [...visibleColumns],
      source: 'ORG_DEFAULT',
    });
    const request = { visibleColumns };
    return this.http
      .put<TableConfiguration>(
        `${this.baseUrl}/${tableId}/org-default`,
        request,
      )
      .pipe(
        tap((config) => this.cache.set(cacheKey, config)),
        catchError((error) => {
          if (previousConfig) {
            this.cache.set(cacheKey, previousConfig);
          } else {
            this.cache.delete(cacheKey);
          }
          return throwError(() => error);
        }),
      );
  }

  deleteOrganizationDefault(tableId: string): Observable<void> {
    this.cache.delete(this.createCacheKey(tableId));
    return this.http.delete<void>(`${this.baseUrl}/${tableId}/org-default`);
  }

  private createCacheKey(tableId: string): string {
    return `${this.getOrganizationCacheKey()}::${tableId}`;
  }

  private getOrganizationCacheKey(): string {
    return this.getCurrentOrganizationId() ?? this.noOrganizationCacheKey;
  }
}
